/* Florame protocol reveal v292 — viewport-synchronised list and wow reveals */
(() => {
  const SELECTOR = '[data-flp-protocol]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function splitRevealText(element) {
    const label = element.textContent.trim();
    const words = label.split(/\s+/);
    let characterIndex = 0;

    element.textContent = '';
    element.setAttribute('aria-label', label);

    words.forEach((word, wordIndex) => {
      const wordMask = document.createElement('span');
      wordMask.className = 'flp-protocol-wow-word';
      wordMask.setAttribute('aria-hidden', 'true');

      Array.from(word).forEach((character) => {
        const characterNode = document.createElement('span');
        characterNode.className = 'flp-protocol-wow-char';
        characterNode.style.setProperty('--flp-char-index', characterIndex);
        characterNode.textContent = character;
        wordMask.appendChild(characterNode);
        characterIndex += 1;
      });

      element.appendChild(wordMask);
      if (wordIndex < words.length - 1) element.appendChild(document.createTextNode(' '));
    });
  }

  function compileShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(message || 'Protocol shader compilation failed');
    }
    return shader;
  }

  function createProgram(gl, vertexSource, fragmentSource) {
    const program = gl.createProgram();
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const message = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(message || 'Protocol shader linking failed');
    }
    return program;
  }

  function createSafeImageReveal(container) {
    let played = false;
    let completionTimer = 0;
    container.classList.add('is-safe-image-ready');

    return {
      play() {
        if (played) return;
        played = true;
        container.classList.add('is-safe-image-playing');
        completionTimer = window.setTimeout(() => {
          container.classList.remove('is-safe-image-playing');
          container.classList.add('is-fluid-complete');
        }, 2900);
      },
      dispose() {
        window.clearTimeout(completionTimer);
        container.classList.remove('is-safe-image-playing');
        container.classList.add('is-fluid-complete');
      },
    };
  }

  async function createFluidReveal(container) {
    const source = container.querySelector('img');
    const canvas = container.querySelector('canvas');
    if (!source || !canvas) return null;

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      powerPreference: 'low-power',
    });
    if (!gl) return null;

    const vertexSource = `
      attribute vec2 aPosition;
      varying vec2 vUv;
      void main() {
        vUv = aPosition * .5 + .5;
        gl_Position = vec4(aPosition, 0., 1.);
      }
    `;

    const fragmentSource = `
      precision highp float;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      uniform vec2 uResolution;
      uniform vec2 uImageSize;
      uniform vec2 uOrigin;
      uniform float uProgress;
      uniform float uTime;

      float softMin(float a, float b, float k) {
        float h = clamp(.5 + .5 * (b - a) / k, 0., 1.);
        return mix(b, a, h) - k * h * (1. - h);
      }

      void main() {
        vec2 coverUv = vUv;
        float frameAspect = uResolution.x / uResolution.y;
        float imageAspect = uImageSize.x / uImageSize.y;
        if (frameAspect > imageAspect) {
          coverUv.y = (coverUv.y - .5) * (imageAspect / frameAspect) + .5;
        } else {
          coverUv.x = (coverUv.x - .5) * (frameAspect / imageAspect) + .5;
        }

        vec2 aspect = vec2(frameAspect, 1.);
        vec2 point = (vUv - uOrigin) * aspect;
        float eased = uProgress * uProgress * (3. - 2. * uProgress);
        float maximumRadius = length(aspect) * 1.04;
        float radius = maximumRadius * eased;
        float angle = atan(point.y, point.x);
        float wave = (
          sin(angle * 3. + uTime * 1.45) +
          .52 * sin(angle * 7. - uTime * .92) +
          .22 * sin(angle * 11. + uTime * .55)
        ) * .018 * (1. - eased * .45);

        float distanceField = length(point) - radius - wave;
        float satelliteRadius = radius * .43;
        distanceField = softMin(distanceField, length(point - vec2(-.23, .10)) - satelliteRadius, .12);
        distanceField = softMin(distanceField, length(point - vec2(.22, .17)) - satelliteRadius * .88, .11);
        distanceField = softMin(distanceField, length(point - vec2(.08, -.24)) - satelliteRadius * .8, .10);

        float feather = mix(.022, .006, eased);
        float mask = (1. - smoothstep(-feather, feather, distanceField)) * smoothstep(0., .025, uProgress);
        vec4 imageColor = texture2D(uTexture, coverUv);
        gl_FragColor = vec4(imageColor.rgb, imageColor.a * mask);
      }
    `;

    let program;
    try {
      program = createProgram(gl, vertexSource, fragmentSource);
    } catch (error) {
      return null;
    }

    source.loading = 'eager';
    source.fetchPriority = 'high';
    if (!source.complete || !source.naturalWidth) {
      try {
        await new Promise((resolve, reject) => {
          source.addEventListener('load', resolve, { once: true });
          source.addEventListener('error', reject, { once: true });
        });
      } catch (error) {
        gl.deleteProgram(program);
        return null;
      }
    }
    try {
      await source.decode();
    } catch (error) {
      if (!source.naturalWidth) {
        gl.deleteProgram(program);
        return null;
      }
    }

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const positionLocation = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

    const uniforms = {
      texture: gl.getUniformLocation(program, 'uTexture'),
      resolution: gl.getUniformLocation(program, 'uResolution'),
      imageSize: gl.getUniformLocation(program, 'uImageSize'),
      origin: gl.getUniformLocation(program, 'uOrigin'),
      progress: gl.getUniformLocation(program, 'uProgress'),
      time: gl.getUniformLocation(program, 'uTime'),
    };
    gl.uniform1i(uniforms.texture, 0);
    gl.uniform2f(uniforms.imageSize, source.naturalWidth, source.naturalHeight);
    gl.uniform2f(uniforms.origin, .64, .48);

    let disposed = false;
    let playing = false;
    let resizeObserver;
    let animationFrame = 0;
    let completionTimer = 0;
    let disposeTimer = 0;

    function resize() {
      if (disposed) return;
      const bounds = container.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(1, Math.round(bounds.width * pixelRatio));
      const height = Math.max(1, Math.round(bounds.height * pixelRatio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      gl.viewport(0, 0, width, height);
      gl.uniform2f(uniforms.resolution, width, height);
    }

    function draw(progress, time) {
      resize();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform1f(uniforms.progress, progress);
      gl.uniform1f(uniforms.time, time);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    function dispose() {
      if (disposed) return;
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(completionTimer);
      window.clearTimeout(disposeTimer);
      resizeObserver?.disconnect();
      gl.deleteTexture(texture);
      gl.deleteBuffer(positionBuffer);
      gl.deleteProgram(program);
    }

    function complete() {
      if (disposed || container.classList.contains('is-fluid-complete')) return;
      playing = false;
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(completionTimer);
      container.classList.remove('is-fluid-playing');
      container.classList.add('is-fluid-complete');
      disposeTimer = window.setTimeout(dispose, 320);
    }

    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    try {
      draw(0, 0);
      container.classList.add('is-fluid-ready');
    } catch (error) {
      dispose();
      return null;
    }

    canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      complete();
    }, { once: true });

    return {
      play() {
        if (disposed || playing || container.classList.contains('is-fluid-complete')) return;
        playing = true;
        container.classList.add('is-fluid-playing');
        const startedAt = performance.now();
        const duration = 2800;
        completionTimer = window.setTimeout(complete, duration + 900);

        function frame(now) {
          if (disposed) return;
          const progress = Math.min(1, (now - startedAt) / duration);
          try {
            draw(progress, (now - startedAt) / 1000);
          } catch (error) {
            complete();
            return;
          }
          if (progress < 1) {
            animationFrame = requestAnimationFrame(frame);
            return;
          }
          complete();
        }

        animationFrame = requestAnimationFrame(frame);
      },
      dispose,
    };
  }

  function initialiseProtocol(section) {
    if (section.dataset.flpProtocolInitialised === 'true') return;
    section.dataset.flpProtocolInitialised = 'true';

    const wowText = section.querySelector('[data-flp-scroll-text]');
    const stampList = section.querySelector('[data-flp-stamp-list]');
    const stampItems = section.querySelectorAll('[data-flp-stamp-list] > li');
    stampItems.forEach((item, index) => item.style.setProperty('--flp-stamp-index', index));
    if (wowText) splitRevealText(wowText);

    if (reduceMotion.matches) {
      section.classList.add('is-protocol-list-visible', 'is-protocol-wow-visible');
      return;
    }

    section.classList.add('flp-protocol-motion-ready');
    let fluidReveal = null;
    let shouldPlayFluid = false;
    const imageContainer = section.querySelector('[data-flp-fluid-reveal]');

    if (imageContainer && !navigator.connection?.saveData) {
      createFluidReveal(imageContainer).then((instance) => {
        fluidReveal = instance || createSafeImageReveal(imageContainer);
        if (shouldPlayFluid) fluidReveal?.play();
      });
    } else if (imageContainer) {
      fluidReveal = createSafeImageReveal(imageContainer);
    }

    const playFluidReveal = () => {
      shouldPlayFluid = true;
      fluidReveal?.play();
    };
    const revealOnce = (target, className, options) => {
      if (!target) {
        section.classList.add(className);
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        section.classList.add(className);
        observer.disconnect();
      }, options);

      observer.observe(target);
    };

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        playFluidReveal();
        imageObserver.disconnect();
      }, { threshold: .16, rootMargin: '0px 0px -6% 0px' });

      if (imageContainer) imageObserver.observe(imageContainer);

      revealOnce(stampList, 'is-protocol-list-visible', {
        threshold: .1,
        rootMargin: '0px 0px -22% 0px',
      });
      revealOnce(wowText, 'is-protocol-wow-visible', {
        threshold: .14,
        rootMargin: '0px 0px -28% 0px',
      });
    } else {
      playFluidReveal();
      section.classList.add('is-protocol-list-visible', 'is-protocol-wow-visible');
    }
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(initialiseProtocol);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
